import { ReactNode } from 'react';

import { Chip, Flex, Text, useThemeColors, ColorsV2 } from '@electricjs/arc';
import { ABMEnrollmentStatusEnum } from '@electricjs/core_entity-client';
import { useTranslation } from 'react-i18next';

import { useGetOrganizationAdditionalInfoQuery } from '@/redux/slices/organizationApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';

type ABMChipContainerProps = {
  children: ReactNode;
};

export const ABMChipContainer = ({ children }: ABMChipContainerProps) => {
  const { t } = useTranslation('getting-started');

  return (
    <Flex columnGap="2rem" alignItems="baseline">
      <Text variant="label-large">{t('abmChipStatus.title')}</Text>
      {children}
    </Flex>
  );
};

const ABMChipStatus = () => {
  const { t } = useTranslation('getting-started');
  const organizationId = useGetOrganizationId();
  const [successChipColor, warningChipColor] = useThemeColors([
    ColorsV2.SUCCESS_LIGHTEST,
    ColorsV2.WARNING_LIGHTEST,
  ]);

  const {
    data: orgAdditionalInfoData,
    isFetching: isFetchingOrgAdditionalInfoData,
  } = useGetOrganizationAdditionalInfoQuery({
    organizationId,
  });

  if (isFetchingOrgAdditionalInfoData) {
    return null;
  }

  switch (orgAdditionalInfoData?.abmStatus) {
    case ABMEnrollmentStatusEnum.Connected:
      return (
        <ABMChipContainer>
          <Chip
            id="abm-enrollment-success-chip"
            backgroundColor={successChipColor}>
            <Text intent="success" variant="label-large">
              {t('abmChipStatus.chipLabel', {
                status: orgAdditionalInfoData?.abmStatus,
              })}
            </Text>
          </Chip>
        </ABMChipContainer>
      );
    case ABMEnrollmentStatusEnum.Unconnected:
      return (
        <ABMChipContainer>
          <Chip
            id="abm-enrollment-warning-chip"
            backgroundColor={warningChipColor}>
            <Text intent="warning" variant="label-large">
              {t('abmChipStatus.chipLabel', {
                status: orgAdditionalInfoData?.abmStatus,
              })}
            </Text>
          </Chip>
        </ABMChipContainer>
      );
    case ABMEnrollmentStatusEnum.Unknown:
    case ABMEnrollmentStatusEnum.NotApplied:
    default:
      return null;
  }
};

export default ABMChipStatus;
